import { Injectable, InjectionToken, Inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Capacitor, registerPlugin } from '@capacitor/core';
import { App } from '@capacitor/app';
import { EMPTY, Observable, merge, of } from 'rxjs';
import { catchError, map, switchMap, withLatestFrom } from 'rxjs/operators';
import { AuthFacade } from '../../auth/state/auth.facade';
import { environment } from '../../../environments/environment';
import { Rating } from '../../models/rating';

// What the home-screen widget shows: the rating from Profile > Statistieken. Kept to exactly the two
// numbers the widget draws - a widget extension has no session of its own, so whatever isn't pushed
// here it simply cannot know.
export interface WidgetSnapshot {
	rating: number;
	delta: number;
}

export interface ProPoolWidgetPlugin {
	// snapshot null clears the widget, which is what logging out has to do: a rating on the home
	// screen outlives the session otherwise.
	publish(options: { snapshot: string | null }): Promise<void>;
}

// The native half lives in frontend/scripts/native-widget/ and is installed into the generated
// ios/android projects by scripts/native-widget-config.mjs.
const ProPoolWidget = registerPlugin<ProPoolWidgetPlugin>('ProPoolWidget');

// The native boundary, swapped for doubles in the spec.
export interface WidgetNative {
	isNative(): boolean;
	foreground(): Observable<void>;
	widget: ProPoolWidgetPlugin;
}

export const WIDGET_NATIVE = new InjectionToken<WidgetNative>('WidgetNative', {
	providedIn: 'root',
	factory: () => ({
		isNative: () => Capacitor.isNativePlatform(),
		foreground: () => new Observable<void>(subscriber => {
			const handle = App.addListener('appStateChange', ({ isActive }) => {
				if (isActive) {
					subscriber.next();
				}
			});
			return () => { handle.then(listener => listener.remove()); };
		}),
		widget: ProPoolWidget,
	})
});

// TODO.md "Homescreen-widget": the app side of it. The widget can't fetch anything itself, so the app
// publishes a snapshot whenever the logged-in user changes and every time it comes back to the
// foreground (a match played elsewhere would leave a stale number otherwise). Web/PWA has no home
// screen widget to feed, so nothing runs there at all.
@Injectable({
	providedIn: 'root'
})
export class WidgetService {

	constructor(
		@Inject(WIDGET_NATIVE) private native: WidgetNative,
		private http: HttpClient,
		private authFacade: AuthFacade) {
		if (!this.native.isNative()) {
			return;
		}

		merge(
			this.authFacade.currentUser$,
			this.native.foreground().pipe(withLatestFrom(this.authFacade.currentUser$), map(([, user]) => user))
		).pipe(
			switchMap(user => user ? this.load(user.id) : of(null))
		).subscribe(snapshot => this.publish(snapshot));
	}

	private load(userId: string): Observable<WidgetSnapshot> {
		return this.http.get<Rating>(`${environment.apiHost}matches/rating/${userId}`).pipe(
			map(rating => ({ rating: rating.currentRating, delta: rating.delta })),
			// Offline or a user without a rating yet: leave whatever the widget already shows rather
			// than blanking it, since the app has nothing better to put there.
			catchError(() => EMPTY)
		);
	}

	private async publish(snapshot: WidgetSnapshot | null): Promise<void> {
		try {
			await this.native.widget.publish({ snapshot: snapshot && JSON.stringify(snapshot) });
		} catch {
			// No widget on this OS version (iOS 16+ / Android 4.1+ with a launcher that has them) -
			// there is nothing the app can do about that, and it must not break the session.
		}
	}
}
